import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';

// Ported from the shadcn `scroll-expansion-hero` snippet.
// Changes from the original:
//   - JS (no TypeScript), no `'use client'`.
//   - `next/image` → plain <img>.
//   - Tailwind palette swapped from blue-200 to green-200 to match Atharva.
//   - Background image given a heavier dark-green tint so light text reads.
//
// Behaviour: as the user scrolls the page, the centred media (image or video)
// expands from a 300×400 box to nearly full-screen. The title text translates
// outward (firstWord left, rest right). Once fully expanded the page resumes
// normal scroll and the children block fades in beneath.

export default function ScrollExpansionHero({
  mediaType = 'image',
  mediaSrc,
  posterSrc,
  bgImageSrc,
  title,
  date,
  scrollToExpand,
  textBlend = false,
  children,
}) {
  const [scrollProgress, setScrollProgress] = useState(0);
  const [showContent, setShowContent] = useState(false);
  const [mediaFullyExpanded, setMediaFullyExpanded] = useState(false);
  const [touchStartY, setTouchStartY] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const sectionRef = useRef(null);

  // Reset state if mediaType changes.
  useEffect(() => {
    setScrollProgress(0);
    setShowContent(false);
    setMediaFullyExpanded(false);
  }, [mediaType]);

  // Wheel + touch handling. While the media isn't fully expanded we intercept
  // scroll and convert it into progress 0→1. Past the threshold the page
  // scrolls normally and the children appear.
  useEffect(() => {
    const handleWheel = (e) => {
      if (mediaFullyExpanded && e.deltaY < 0 && window.scrollY <= 5) {
        setMediaFullyExpanded(false);
        e.preventDefault();
      } else if (!mediaFullyExpanded) {
        e.preventDefault();
        const delta = e.deltaY * 0.0009;
        const next = Math.min(Math.max(scrollProgress + delta, 0), 1);
        setScrollProgress(next);
        if (next >= 1) {
          setMediaFullyExpanded(true);
          setShowContent(true);
        } else if (next < 0.75) {
          setShowContent(false);
        }
      }
    };

    const handleTouchStart = (e) => {
      setTouchStartY(e.touches[0].clientY);
    };

    const handleTouchMove = (e) => {
      if (!touchStartY) return;
      const touchY = e.touches[0].clientY;
      const deltaY = touchStartY - touchY;
      if (mediaFullyExpanded && deltaY < -20 && window.scrollY <= 5) {
        setMediaFullyExpanded(false);
        e.preventDefault();
      } else if (!mediaFullyExpanded) {
        e.preventDefault();
        const factor = deltaY < 0 ? 0.008 : 0.005;
        const delta = deltaY * factor;
        const next = Math.min(Math.max(scrollProgress + delta, 0), 1);
        setScrollProgress(next);
        if (next >= 1) {
          setMediaFullyExpanded(true);
          setShowContent(true);
        } else if (next < 0.75) {
          setShowContent(false);
        }
        setTouchStartY(touchY);
      }
    };

    const handleTouchEnd = () => setTouchStartY(0);

    const handleScroll = () => {
      if (!mediaFullyExpanded) window.scrollTo(0, 0);
    };

    window.addEventListener('wheel', handleWheel, { passive: false });
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('touchstart', handleTouchStart, { passive: false });
    window.addEventListener('touchmove', handleTouchMove, { passive: false });
    window.addEventListener('touchend', handleTouchEnd);
    return () => {
      window.removeEventListener('wheel', handleWheel);
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, [scrollProgress, mediaFullyExpanded, touchStartY]);

  // Mobile breakpoint
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  const mediaWidth = 300 + scrollProgress * (isMobile ? 650 : 1250);
  const mediaHeight = 400 + scrollProgress * (isMobile ? 200 : 400);
  // Title fades out as the image grows. Multiplier > 1 so it's fully gone
  // before the image hits its max — keeps the expand feeling clean.
  const titleOpacity = Math.max(0, 1 - scrollProgress * 1.6);
  const subOpacity = Math.max(0, 1 - scrollProgress * 2.2);

  const firstWord = title ? title.split(' ')[0] : '';
  const restOfTitle = title ? title.split(' ').slice(1).join(' ') : '';

  const isYouTube = mediaType === 'video' && mediaSrc?.includes('youtube.com');

  return (
    <div
      ref={sectionRef}
      className="overflow-x-hidden transition-colors duration-700 ease-in-out"
    >
      <section className="relative flex min-h-[100dvh] flex-col items-center justify-start">
        <div className="relative flex w-full flex-col items-center min-h-[100dvh]">
          {/* Background image with dark-green tint */}
          <motion.div
            className="absolute inset-0 z-0 h-full"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 - scrollProgress }}
            transition={{ duration: 0.1 }}
          >
            <img
              src={bgImageSrc}
              alt=""
              aria-hidden="true"
              className="h-screen w-screen object-cover object-center"
              loading="eager"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-green-950/70 via-green-900/55 to-emerald-950/70" />
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(34,197,94,0.18),transparent_60%)]" />
          </motion.div>

          <div className="container relative z-10 mx-auto flex flex-col items-center justify-start">
            <div className="relative flex h-[100dvh] w-full items-center justify-center">

              {/* FPO — sits ABOVE the image, pinned near the top of the
                  viewport. Stays in place while the image expands; fades
                  out via titleOpacity. */}
              <h1
                className={`absolute left-1/2 top-[15%] z-20 -translate-x-1/2 whitespace-nowrap pb-2 font-serif text-4xl font-black leading-[1.1] tracking-[-0.045em] text-white drop-shadow-[0_6px_24px_rgba(0,0,0,0.6)] transition-none sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl ${
                  textBlend ? 'mix-blend-difference' : ''
                }`}
                style={{ opacity: titleOpacity }}
              >
                {firstWord}
              </h1>

              {/* Expanding media — centered, in front of the title, expands on scroll */}
              <div
                className="relative z-10 rounded-2xl transition-none"
                style={{
                  width: `${mediaWidth}px`,
                  height: `${mediaHeight}px`,
                  maxWidth: '95vw',
                  maxHeight: '85vh',
                  boxShadow: '0 0 80px rgba(0,0,0,0.45)',
                }}
              >
                {mediaType === 'video' ? (
                  isYouTube ? (
                    <div className="pointer-events-none relative h-full w-full">
                      <iframe
                        title={title || 'video'}
                        width="100%"
                        height="100%"
                        src={
                          mediaSrc.includes('embed')
                            ? mediaSrc +
                              (mediaSrc.includes('?') ? '&' : '?') +
                              'autoplay=1&mute=1&loop=1&controls=0&showinfo=0&rel=0&disablekb=1&modestbranding=1'
                            : mediaSrc.replace('watch?v=', 'embed/') +
                              '?autoplay=1&mute=1&loop=1&controls=0&showinfo=0&rel=0&disablekb=1&modestbranding=1&playlist=' +
                              mediaSrc.split('v=')[1]
                        }
                        className="h-full w-full rounded-xl"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                      <motion.div
                        className="absolute inset-0 rounded-xl bg-black/30"
                        initial={{ opacity: 0.7 }}
                        animate={{ opacity: 0.5 - scrollProgress * 0.3 }}
                        transition={{ duration: 0.2 }}
                      />
                    </div>
                  ) : (
                    <div className="pointer-events-none relative h-full w-full">
                      <video
                        src={mediaSrc}
                        poster={posterSrc}
                        autoPlay
                        muted
                        loop
                        playsInline
                        preload="auto"
                        className="h-full w-full rounded-xl object-cover"
                        controls={false}
                        disablePictureInPicture
                        disableRemotePlayback
                      />
                      <motion.div
                        className="absolute inset-0 rounded-xl bg-black/30"
                        initial={{ opacity: 0.7 }}
                        animate={{ opacity: 0.5 - scrollProgress * 0.3 }}
                        transition={{ duration: 0.2 }}
                      />
                    </div>
                  )
                ) : (
                  <div className="relative h-full w-full">
                    <img
                      src={mediaSrc}
                      alt={title || 'Media content'}
                      className="h-full w-full rounded-xl object-cover"
                    />
                    <motion.div
                      className="absolute inset-0 rounded-xl bg-black/40"
                      initial={{ opacity: 0.7 }}
                      animate={{ opacity: 0.55 - scrollProgress * 0.3 }}
                      transition={{ duration: 0.2 }}
                    />
                  </div>
                )}

              </div>

              {/* supply, buyer demand — sits BELOW the image, pinned near
                  the bottom of the viewport. Italic gradient serif so it
                  contrasts with FPO's bold-black above. */}
              <h1
                className={`absolute bottom-[18%] left-1/2 z-20 -translate-x-1/2 whitespace-nowrap bg-gradient-to-r from-green-100 via-emerald-200 to-green-300 bg-clip-text px-2 pb-3 pt-1 text-center font-serif text-2xl font-light italic leading-[1.25] tracking-[-0.015em] text-transparent drop-shadow-[0_6px_24px_rgba(0,0,0,0.6)] transition-none sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl ${
                  textBlend ? 'mix-blend-difference' : ''
                }`}
                style={{ opacity: titleOpacity }}
              >
                {restOfTitle}
              </h1>

              {/* Date + scroll-cue pinned to viewport corners, fading out
                  on the same curve as the title. */}
              <div
                className="pointer-events-none absolute inset-x-0 bottom-10 z-20 flex items-center justify-between px-8 transition-none md:px-16"
                style={{ opacity: subOpacity }}
              >
                {date ? (
                  <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-green-200/90 sm:text-xs">
                    {date}
                  </p>
                ) : <span />}
                {scrollToExpand ? (
                  <p className="text-[11px] font-medium text-green-200/80 sm:text-xs">
                    {scrollToExpand}
                  </p>
                ) : <span />}
              </div>

            </div>

            <motion.section
              className="flex w-full flex-col px-8 py-10 md:px-16 lg:py-20"
              initial={{ opacity: 0 }}
              animate={{ opacity: showContent ? 1 : 0 }}
              transition={{ duration: 0.7 }}
            >
              {children}
            </motion.section>
          </div>
        </div>
      </section>
    </div>
  );
}
